<script setup lang="ts">
/**
 * ImpersonationBanner — красный баннер вверху кабинета во время активной
 * импертсонации staff'ом (inbox-274 / Task 9 console-plan).
 *
 * Source: useMeStore.me.impersonation (заполняется backend в GET /me когда
 * session row имеет imp_session_id, backend inbox-267 + 301).
 *
 * UI:
 *   - fixed top, full-width, accent-danger background (rgba(255,93,143,0.18))
 *   - target user name + countdown (mm:ss до expires_at)
 *   - кнопка «Остановить» → POST /api/v1/console/impersonate/stop → reload
 *
 * Mount: внутри CabinetLayout (рендерится поверх контента). На public-страницах
 * не показывается (логично — импертсонация работает только в cabinet flow).
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { req } from '@/api/client';
import { useMeStore } from '@/stores/me';

const me = useMeStore();
const stopping = ref(false);
const errorMsg = ref<string | null>(null);

// Tick раз в секунду чтобы countdown обновлялся. ref для триггера re-render.
const now = ref(Date.now());
let tickTimer: ReturnType<typeof setInterval> | null = null;

const imp = computed(() => me.me?.impersonation || null);
const expiresMs = computed(() => (imp.value ? Date.parse(imp.value.expires_at) : 0));
const remainingSec = computed(() => Math.max(0, Math.floor((expiresMs.value - now.value) / 1000)));
const remainingFmt = computed(() => {
  const s = remainingSec.value;
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, '0')}`;
});
const expired = computed(() => remainingSec.value <= 0 && !!imp.value);

async function stop(): Promise<void> {
  stopping.value = true;
  errorMsg.value = null;
  try {
    await req<{ ok: true }>('/api/v1/console/impersonate/stop', { method: 'POST' });
    // Reload страницы — backend ставит обратно оригинальную staff-сессию через
    // cookie. SPA state нужно полностью обновить.
    if (typeof window !== 'undefined') window.location.reload();
  } catch (e) {
    errorMsg.value = (e as Error).message || 'Не удалось остановить';
    stopping.value = false;
  }
}

onMounted(() => {
  tickTimer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  if (tickTimer) clearInterval(tickTimer);
});
</script>

<template>
  <Transition
    enter-active-class="banner-enter-active"
    leave-active-class="banner-leave-active"
    enter-from-class="banner-enter-from"
    leave-to-class="banner-leave-to"
  >
    <div v-if="imp" class="impersonation-banner" data-testid="impersonation-banner" role="alert">
      <div class="banner-inner">
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 9v4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
          </svg>
        </span>
        <div class="banner-text">
          <strong>Имперсонация активна.</strong>
          Вы работаете под пользователем
          <span class="target">#{{ imp.target_user_id }}</span>.
          Все действия логируются — причина: «{{ imp.reason }}».
        </div>
        <div class="banner-meta">
          <span class="countdown" :class="{ critical: remainingSec < 60 }">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {{ expired ? 'истекла' : remainingFmt }}
          </span>
          <button
            type="button"
            class="stop-btn"
            :disabled="stopping"
            @click="stop"
          >
            {{ stopping ? 'Останавливаем…' : 'Остановить' }}
          </button>
        </div>
      </div>
      <div v-if="errorMsg" class="banner-error">⚠ {{ errorMsg }}</div>
    </div>
  </Transition>
</template>

<style scoped>
.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: linear-gradient(180deg, rgba(255, 93, 143, 0.20), rgba(255, 93, 143, 0.12));
  border-bottom: 1px solid rgba(255, 93, 143, 0.40);
  color: #ffd6e3;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.banner-inner {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 24px;
  font-size: 13px;
  line-height: 1.45;
}
.icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 93, 143, 0.25);
  color: #ff5d8f;
  flex-shrink: 0;
}
.banner-text {
  flex: 1;
  min-width: 0;
  color: #f5e0e8;
}
.banner-text strong { color: #ff5d8f; font-weight: 700; }
.banner-text .target {
  font-family: 'SF Mono', Menlo, monospace;
  background: rgba(0, 0, 0, 0.25);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.banner-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.countdown {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 100px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #ffd6e3;
}
.countdown.critical { color: #ff5d8f; background: rgba(255, 93, 143, 0.20); }
.stop-btn {
  background: #ff5d8f;
  color: #fff;
  border: 0;
  padding: 8px 16px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-height: 36px;
}
.stop-btn:hover:not(:disabled) { background: #ff7da3; }
.stop-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.banner-error {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 8px;
  font-size: 12px;
  color: #ffb1c4;
}

@media (max-width: 760px) {
  .banner-inner { padding: 10px 14px; gap: 10px; flex-wrap: wrap; }
  .banner-text { font-size: 12.5px; }
  .icon { width: 28px; height: 28px; }
  .banner-meta { width: 100%; justify-content: space-between; }
}

.banner-enter-active, .banner-leave-active { transition: transform 0.2s ease, opacity 0.2s ease; }
.banner-enter-from, .banner-leave-to { transform: translateY(-100%); opacity: 0; }
</style>
